import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import { Box, Divider, styled } from '@mui/material';

import {
  ReservationFormInput,
  ReservationFormValues,
} from '../schema/reservationForm.schema';

import { usePortalContext } from '@/contexts/PortalProvider';

import { useAppSelector } from '@/hooks/useAppSelector';
import { useLookups } from '@/hooks/useLookups';

import * as lookupSlice from '@/data/lookup.slice';

import TextInput from '@/components/forms/TextInput';
import Checkbox from '@/components/forms/Checkbox';
import SelectMenu from '@/components/forms/SelectMenu';
import { PhoneInputField } from './PhoneInputField';

const PREFIX = 'AlternateContact';

const classes = {
  subctrl: `${PREFIX}-subctrl`,
  names: `${PREFIX}-names`,
  name: `${PREFIX}-name`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),

  [`& .${classes.subctrl}`]: {
    marginLeft: theme.spacing(4),
  },
  [`& .${classes.names}`]: {
    marginLeft: theme.spacing(4),
    display: 'flex',
    gap: theme.spacing(1),
  },
  [`& .${classes.name}`]: {
    flexGrow: 1,
  },
}));

type AlternateContactProps = {
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>;
};

const AlternateContact = ({ form }: AlternateContactProps) => {
  const { t } = useTranslation('common');
  const { isPortal } = usePortalContext();
  const requiredFieldMessage = t('common.requiredField');

  useLookups(['alternateContactType']);
  const alternateContactTypes = useAppSelector(
    lookupSlice.selectAlternateContactTypes,
  );

  const alternateContact = form.watch('AlternateContact');

  // If the reservation is being made through a portal, do not show the alternate contact fields.
  if (isPortal) {
    return null;
  }

  return (
    <StyledBox>
      <Divider />
      <Checkbox
        name={`AlternateContact`}
        label={t('reservation.alternateTextee')}
        control={form.control as any}
      />
      {alternateContact && (
        <>
          <SelectMenu
            className={classes.subctrl}
            label={t('reservation.alternateContactType')}
            name={`AlternateContactType`}
            values={alternateContactTypes}
            valueAttr="alternateContactTypeBC"
            labelAttr="alternateContactTypeName"
            required={true}
            control={form.control as any}
            errorMessage={
              form.formState.errors?.AlternateContactType?.message as string
            }
            rules={{ required: requiredFieldMessage }}
          />
          <Box className={classes.names}>
            <TextInput
              className={classes.name}
              name={`AlternateContactFirstName`}
              label={t('reservation.alternateTexteeFirstName')}
              control={form.control as any}
              required={true}
              rules={{ required: requiredFieldMessage }}
              errors={form.formState.errors}
            />
            <TextInput
              className={classes.name}
              name={`AlternateContactLastName`}
              label={t('reservation.alternateTexteeLastName')}
              control={form.control as any}
              required={true}
              rules={{ required: requiredFieldMessage }}
              errors={form.formState.errors}
            />
          </Box>
          <PhoneInputField
            tripField="AlternateContactPhone"
            label={t('reservation.alternateTexteePhone')}
            required={true}
            className={classes.subctrl}
          />
          <Checkbox
            className={classes.subctrl}
            name={`AlternateContactAllowSMS`}
            label={t('reservation.allowSMS')}
            control={form.control as any}
          />
        </>
      )}
    </StyledBox>
  );
};

export { AlternateContact };
